"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ClipboardServiceInstance = void 0;
const client_1 = require("@rws-framework/client");
class ClipboardService extends client_1.RWSService {
    constructor() {
        super(...arguments);
        this.defaultOptions = {
            successMessage: 'Copied to clipboard!',
            errorMessage: 'Failed to copy to clipboard',
            successDuration: 2000
        };
    }
    /**
     * Copy text to clipboard with modern API fallback
     * @param text - Text to copy
     * @param options - Optional configuration
     * @returns Promise with copy result
     */
    async copyText(text, options = {}) {
        const opts = { ...this.defaultOptions, ...options };
        try {
            // Try modern clipboard API first
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
                return {
                    success: true,
                    message: opts.successMessage
                };
            }
            else {
                // Fallback to execCommand
                return this.fallbackCopy(text, opts);
            }
        }
        catch (error) {
            console.error('Clipboard copy failed:', error);
            // Try fallback on error
            return this.fallbackCopy(text, opts);
        }
    }
    /**
     * Fallback copy method using document.execCommand
     * @param text - Text to copy
     * @param options - Configuration options
     * @returns Copy result
     */
    fallbackCopy(text, options) {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        textArea.style.top = '-1000px';
        textArea.style.left = '-1000px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
            const successful = document.execCommand('copy');
            document.body.removeChild(textArea);
            if (successful) {
                return {
                    success: true,
                    message: options.successMessage
                };
            }
            else {
                return {
                    success: false,
                    message: options.errorMessage
                };
            }
        }
        catch (error) {
            document.body.removeChild(textArea);
            console.error('Fallback copy failed:', error);
            return {
                success: false,
                message: options.errorMessage
            };
        }
    }
    /**
     * Copy text and automatically show feedback with observable pattern
     * @param text - Text to copy
     * @param callback - Optional callback with success state and duration
     * @param options - Optional configuration
     */
    async copyWithFeedback(text, callback, options = {}) {
        const opts = { ...this.defaultOptions, ...options };
        const result = await this.copyText(text, opts);
        if (callback) {
            callback(result.success, opts.successDuration || 2000);
        }
        return result;
    }
    /**
     * Check if clipboard API is available
     * @returns boolean indicating clipboard availability
     */
    isClipboardSupported() {
        return !!(navigator.clipboard || document.execCommand);
    }
    /**
     * Read text from clipboard (if permissions allow)
     * @returns Promise with clipboard text or null
     */
    async readText() {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                return await navigator.clipboard.readText();
            }
        }
        catch (error) {
            console.warn('Reading from clipboard failed:', error);
        }
        return null;
    }
}
exports.ClipboardServiceInstance = ClipboardService;
exports.default = ClipboardService.getSingleton();
